import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { ordersService } from '@/services/orders.service';
import { errorMessage } from '@/lib/api';
import { qk } from '@/lib/query-keys';
import type { MaintenanceEntry } from '@/types/api';
import { MaintenanceFilePicker } from './maintenance-file-picker';
import { MaintenanceVisibilitySelect } from './maintenance-visibility';
import type { MaintenanceVisibility } from '@oficina/shared';

export function MaintenanceEditDialog({ orderId, entry }: { orderId: string; entry: MaintenanceEntry }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(entry.description);
  const [files, setFiles] = useState<File[]>([]);
  const [visibility, setVisibility] = useState<MaintenanceVisibility>(entry.visibility ?? 'INTERNAL');
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => ordersService.updateMaintenance(orderId, entry.id, description.trim(), files, visibility),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orders.detail(orderId) });
      void qc.invalidateQueries({ queryKey: qk.clients.all });
      void qc.invalidateQueries({ queryKey: qk.equipment.all });
      setOpen(false);
      setFiles([]);
      toast.success('Registro atualizado.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (save.isPending) return;
        if (next) {
          setDescription(entry.description);
          setVisibility(entry.visibility ?? 'INTERNAL');
          setFiles([]);
        }
        setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="icon-sm" title="Editar comentário ou adicionar mídia" aria-label={`Editar serviço: ${entry.description}`}>
          <Pencil aria-hidden />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar serviço realizado</DialogTitle>
          <DialogDescription>Atualize seu comentário ou adicione fotos e vídeos a este registro.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Label htmlFor={`edit-maintenance-${entry.id}`}>Serviço realizado</Label>
          <div className="mt-1.5 flex flex-col gap-3 sm:flex-row sm:items-start">
            <Textarea
              id={`edit-maintenance-${entry.id}`}
              className="min-h-28 flex-1"
              maxLength={2000}
              value={description}
              disabled={save.isPending}
              onChange={(event) => setDescription(event.target.value)}
            />
            <MaintenanceFilePicker files={files} onChange={setFiles} disabled={save.isPending} />
          </div>
          <div className="mt-3">
            <MaintenanceVisibilitySelect
              idPrefix={`edit-maintenance-${entry.id}`}
              value={visibility}
              onChange={setVisibility}
              disabled={save.isPending}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={save.isPending} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            loading={save.isPending}
            disabled={description.trim().length < 2}
            onClick={() => save.mutate()}
          >
            <Save /> Salvar alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
